import axios from 'axios';

import {createUrl} from './formatters';

// Keep reporting off the shared Axios instance. Otherwise a failure from the
// logging endpoint would pass through the service-error interceptor and recurse.
const reportingClient = axios.create();
let axiosInterceptorInstalled = false;
const SENSITIVE_KEYS = /authorization|cookie|password|secret|token|hash|salt/i;

const sanitizeValue = (value, depth = 0, seen = new WeakSet()) => {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string') return value.slice(0, 1000);
    if (typeof value !== 'object') return value;
    if (depth >= 4) return '[Max depth reached]';
    if (seen.has(value)) return '[Circular]';

    seen.add(value);
    if (Array.isArray(value)) {
        return value.slice(0, 20).map(item => sanitizeValue(item, depth + 1, seen));
    }

    return Object.keys(value).slice(0, 30).reduce((result, key) => {
        result[key] = SENSITIVE_KEYS.test(key)
            ? '[REDACTED]'
            : sanitizeValue(value[key], depth + 1, seen);
        return result;
    }, {});
};

const summarizeResponse = data => {
    try {
        return JSON.stringify(sanitizeValue(data)).slice(0, 8000);
    } catch (error) {
        return '[Unable to serialize response]';
    }
};

const serializeError = error => error && ({
    name: error.name,
    code: error.code,
    message: error.message,
    stack: error.stack
});

const isSuppressedServiceError = error => {
    if (!error) return false;
    const status = error.response && error.response.status;
    const expectedStatuses = (error.config && error.config.expectedErrorStatuses) || [];
    const isOfflineNetworkFailure = !error.response && !navigator.onLine;
    return axios.isCancel(error) || status === 401 || expectedStatuses.includes(status) || isOfflineNetworkFailure;
};

// Reporting is best-effort: telemetry must not create an unhandled rejection
// or interfere with the user-facing error path.
export const logEvent = (level, message, {error, context} = {}) => reportingClient.post(createUrl('/logs/client'), {
    level,
    message: message || (error && error.message) || 'Unknown client event',
    error: serializeError(error),
    context: {
        ...context,
        page: window.location.pathname
    }
}, {
    // axios.create() does not receive authorization defaults added after login.
    headers: {authorization: axios.defaults.headers.common.authorization}
}).catch(reportingError => console.error('Unable to report client event:', reportingError));

export const logError = (error, context) => {
    if (error && (error.__logReported || isSuppressedServiceError(error))) return Promise.resolve();
    if (error) error.__logReported = true;
    return logEvent('error', error && error.message, {error, context});
};
export const logWarning = (message, context) => logEvent('warning', message, {context});
export const logInfo = (message, context) => logEvent('info', message, {context});

const getServiceErrorContext = error => {
    const {config = {}, response} = error;
    return {
        type: 'service-call-failure',
        method: config.method && config.method.toUpperCase(),
        url: config.url,
        status: response && response.status,
        statusText: response && response.statusText,
        responseSummary: response && summarizeResponse(response.data),
        timeout: config.timeout,
        browserOnline: navigator.onLine
    };
};

export const installAxiosErrorLogging = () => {
    if (axiosInterceptorInstalled) return;
    axiosInterceptorInstalled = true;

    axios.interceptors.response.use(
        response => response,
        error => {
            const status = error.response && error.response.status;
            const expectedStatuses = (error.config && error.config.expectedErrorStatuses) || [];
            const isExpectedStatus = expectedStatuses.includes(status);
            const isCancelled = axios.isCancel(error);
            const isUnauthorized = status === 401;
            const isOnlineNetworkFailure = !error.response && navigator.onLine;
            const shouldReportError = !isCancelled && (isOnlineNetworkFailure || status >= 500);
            const shouldReportWarning = !isCancelled && !isExpectedStatus &&
                status >= 400 && status < 500 && status !== 401;

            if (shouldReportError) {
                logError(error, getServiceErrorContext(error));
            } else if (shouldReportWarning && !error.__logReported) {
                error.__logReported = true;
                const message = status === 429
                    ? 'Client service call was rate limited'
                    : `Client service call returned HTTP ${status}`;
                logWarning(message, getServiceErrorContext(error));
            } else if (isCancelled || isExpectedStatus || isUnauthorized || !navigator.onLine) {
                // Prevent the global unhandled-rejection handler from reporting
                // a service failure that this interceptor intentionally ignored.
                error.__logReported = true;
            }

            return Promise.reject(error);
        }
    );
};

export const installGlobalErrorHandlers = () => {
    window.addEventListener('error', event => logError(event.error || new Error(event.message), {
        filename: event.filename,
        line: event.lineno,
        column: event.colno
    }));
    window.addEventListener('unhandledrejection', event => {
        const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
        logError(error, {type: 'unhandledrejection'});
    });
};
